import React from 'react'
import PropTypes from 'prop-types'
import { Grid, Typography, useTheme, useMediaQuery, Box, Paper } from '@material-ui/core'

import { CustomTable } from 'components'
import { getLabelEndereco, getLabelRegiaoAnatomica } from 'helpers/labels'
import { MapaCorpo } from 'views/ExamesRealizados/components/MapaCorpo/MapaCorpo'
import { useStyles } from './FormLesoesTraumaticas.styles'
import { useFormLesoesTraumaticas } from './hooks/useFormLesoesTraumaticas'

export const FormLesoesTraumaticas = ({ idExameRealizado, idCadaver, setLesoesForm, setSvgLesoesForm }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const { processandoLesoes, lesoes, setLesoes, setSVGElements } = useFormLesoesTraumaticas({
    idExameRealizado,
    idCadaver,
    setLesoesForm,
    setSvgLesoesForm,
  })

  const getLabelLesao = (rowData) => {
    if (rowData.tipo?.nome?.toLowerCase() === 'outro') {
      return rowData.tipoTextoLivre
    }
    return rowData.tipo?.nome || '-'
  }

  const getLabelCaracteristicasLesao = (rowData) => {
    const tipos = {
      TIRO: [
        { field: 'pafTipoBorda', label: 'tipo borda' },
        { field: 'pafHaloEnxugo', label: 'halo de enxugo' },
        { field: 'pafAureolaEquimotica', label: 'auréola equimótica' },
        { field: 'pafOrlaEscoriacao', label: 'orla de escoriação' },
        { field: 'pafZonaTatuagem', label: 'zona de tatuagem' },
        { field: 'pafZonaEsfumacamento', label: 'zona de esfumaçamento' },
        { field: 'pafZonaQueimadura', label: 'zona de queimadura' },
        { field: 'pafAspectoEstrelado', label: 'aspecto estrelado' },
        { field: 'pafMarcaCano', label: 'marca de cano' },
      ],
      INCISAO: [
        { field: 'incisaoRegiaoMaisAprofundada', label: 'região mais aprofundada' },
        { field: 'incisaoRegiaoCaudaEscoriacao', label: 'região da cauda de escoriação' },
        { field: 'incisaoBorda', label: 'borda' },
      ],
      PERFUROINCISA: [
        { field: 'perfuroIncisaForma', label: 'forma' },
        { field: 'incisaoBorda', label: 'borda' },
      ],
      QUEIMADURA_ELETRICA: [
        { field: 'tipoQueimaduraEletrica', label: 'tipo da queimadura elétrica' },
        { field: 'metalizacao', label: 'metalizacao' },
        { field: 'eritema', label: 'eritema' },
        { field: 'flictema', label: 'flictema' },
        { field: 'carbonizacao', label: 'carbonizacao' },
        { field: 'necrose', label: 'necrose' },
      ],
      COGUMELO_ESPUMA: [{ field: 'tipoCogumeloEspuma', label: 'tipo do cogumelo de espuma' }],
      SULCO: [
        { field: 'sulcoOrientacao', label: 'orientação' },
        { field: 'sulcoMultiplicidade', label: 'multiplicidade' },
        { field: 'sulcoContinuidade', label: 'continuidade' },
        { field: 'sulcoTonalidade', label: 'tonalidade' },
        { field: 'sulcoTumefeito', label: 'tumefeito' },
        { field: 'sulcoProfundidade', label: 'profundidade' },
        { field: 'sulcoMargensElevadas', label: 'margens elevadas' },
        { field: 'sulcoLeitoDeprimido', label: 'leito deprimido' },
        { field: 'sulcoPeleEnrugada', label: 'pele enrugada' },
      ],
    }

    const listaCaraceteristicas = Object.entries(tipos).flatMap(([tipo, campos]) =>
      rowData?.tipo?.categoria === tipo || rowData?.tipo?.cod === tipo
        ? campos
          .filter(({ field }) => rowData[field])
          .map(
            ({ label, field }) =>
              `${label}${typeof rowData[field] === 'boolean' ? '' : `: ${rowData[field].toLowerCase()}`
              }`
          )
        : []
    )
    const medida = rowData.medida ? [`medida:${rowData.medida} mm`] : []
    return medida.concat(listaCaraceteristicas).join('; ')
  }

  const columns = [
    {
      title: 'Identificador',
      render: (rowData, index) => (
        <Typography className={classes.linhaTabela}>Lesão {rowData.tableData?.id != null ? rowData.tableData.id + 1 : lesoes.indexOf(rowData) + 1}</Typography>
      ),
    },
    {
      title: 'Tipo da lesão',
      render: (rowData) => (
        <Typography className={classes.linhaTabela}>{getLabelLesao(rowData)}</Typography>
      ),
    },
    {
      title: 'Características específicas',
      render: (rowData) => (
        <Typography className={classes.linhaTabela}>
          {getLabelCaracteristicasLesao(rowData)}
        </Typography>
      ),
    },
    {
      title: 'Região',
      render: (rowData) => (
        <Typography className={classes.linhaTabela}>
          {getLabelRegiaoAnatomica(rowData.regioes?.[0])}
        </Typography>
      ),
    },
    {
      title: 'Observações',
      render: (rowData) => <Typography>{rowData.observacoes || ' - '}</Typography>,
    },
  ]

  return (
    <div>
      {!processandoLesoes && (
        <MapaCorpo lesoes={lesoes} setLesoes={setLesoes} setSVGElements={setSVGElements} />
      )}
      <Grid className={classes.fieldGroup} alignItems="center" container justify="space-between">
        <Grid
          className={classes.dados}
          alignItems="center"
          container
          justify="space-between"
          spacing={1}
        >
          <Grid item md={12} sm={12} xs={12} className={classes.tableContainer}>
            {isMobile ? (
              lesoes && lesoes.length > 0 ? (
                <Grid container spacing={2}>
                  {lesoes.map((item, index) => (
                    <Grid item xs={12} key={item.id || index}>
                      <Paper className={classes.lesaoCard}>
                        <Box mb={1.5}>
                          <Typography
                            variant="subtitle2"
                            color="secondary"
                            style={{ fontWeight: 'bold', fontSize: '14px' }}
                          >
                            Lesão {index + 1}
                          </Typography>
                        </Box>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="body2" style={{ fontSize: '12px' }}>
                              <b>Tipo:</b> {getLabelLesao(item)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" style={{ fontSize: '12px' }}>
                              <b>Região:</b> {getLabelRegiaoAnatomica(item.regioes?.[0])}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Box mt={0.5}>
                              <Typography variant="body2" style={{ fontSize: '12px' }}>
                                <b>Características:</b> {getLabelCaracteristicasLesao(item) || '-'}
                              </Typography>
                            </Box>
                          </Grid>
                          {item.observacoes && (
                            <Grid item xs={12}>
                              <Box
                                mt={0.5}
                                pt={0.5}
                                style={{ borderTop: `1px dashed ${theme.palette.divider}` }}
                              >
                                <Typography variant="body2" style={{ fontSize: '12px' }}>
                                  <b>Observações:</b> {item.observacoes}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography
                  style={{
                    fontSize: '12px',
                    padding: '24px',
                    textAlign: 'center',
                    color: '#999',
                    fontStyle: 'italic',
                  }}
                >
                  Nenhuma lesão registrada
                </Typography>
              )
            ) : (
              <CustomTable
                title=""
                columns={columns}
                data={lesoes}
                isLoading={processandoLesoes}
                emptyDataSourceMessage="Nenhuma lesão registrada"
                options={{
                  search: false,
                  pageSize: 10,
                  paging: false,
                  headerStyle: {
                    backgroundColor: '#f4f6f8',
                    fontSize: '12px',
                  },
                  toolbar: false,
                }}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

FormLesoesTraumaticas.propTypes = {
  idExameRealizado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  idCadaver: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setLesoesForm: PropTypes.func.isRequired,
  setSvgLesoesForm: PropTypes.func.isRequired,
}
